import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Box, Typography, Skeleton } from "@mui/material";
import {
  Groups as GroupsIcon,
  Person as PersonIcon,
  ChevronRight as ChevronIcon,
  ArrowUpward as UpArrowIcon,
} from "@mui/icons-material";
import { useTheme } from "./ThemeContext";

const API_BASE_URL = process.env.REACT_APP_SC_API_BASE_URL || "http://localhost:8080";

// Rank emojis for top contributors (by position)
const RANK_EMOJIS = ["🏆", "👑", "📊", "💎", "⭐"];
// Fallback emojis for communities (when no icon data available)
const COMMUNITY_EMOJIS = ["🚀", "📈", "💰", "📊", "🔥"];

function formatCount(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n);
}

// ── Shared card shell ────────────────────────────────────────────────────────
function SectionCard({ children }) {
  return (
    <Box
      sx={{
        backgroundColor: "#1a1f26",
        border: "1px solid #1e2939",
        borderRadius: "14px",
        overflow: "hidden",
        mb: 2,
        // prevent inner content from ever overflowing the card width
        minWidth: 0,
      }}
    >
      {children}
    </Box>
  );
}

// ── Section header with gradient icon box ───────────────────────────────────
function SectionHeader({ icon: Icon, title, gradient, emoji }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        pt: 1.5,
        pb: 1.5,
        borderBottom: "1px solid #1e2939",
      }}
    >
      {emoji ? (
        <Typography sx={{ fontSize: "1.1rem", lineHeight: 1 }}>{emoji}</Typography>
      ) : (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "10px",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 16, color: "#fff" }} />
        </Box>
      )}
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "0.875rem",
          color: "#f3f4f6",
          letterSpacing: "-0.01em",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}

// ── Ticker row ───────────────────────────────────────────────────────────────
function TickerRow({ ticker }) {
  const positive = (ticker.changePercent ?? 0) >= 0;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 1,
        height: 36,
        borderRadius: "10px",
        backgroundColor: "#0f1419",
        minWidth: 0,
      }}
    >
      {/* Symbol + mention count */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          flex: 1,
          overflow: "hidden",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.875rem",
            color: "#f3f4f6",
            letterSpacing: "-0.01em",
            whiteSpace: "nowrap",
          }}
        >
          {ticker.symbol ? `$${ticker.symbol}` : ticker.name}
        </Typography>
        {ticker.mentionCount != null && (
          <Typography
            sx={{ fontSize: "0.75rem", color: "#6a7282", whiteSpace: "nowrap" }}
          >
            {formatCount(ticker.mentionCount)}
          </Typography>
        )}
      </Box>
      {/* % change */}
      {ticker.changePercent != null && (
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 500,
            color: positive ? "#05df72" : "#ff6467",
            whiteSpace: "nowrap",
            flexShrink: 0,
            ml: 1,
          }}
        >
          {positive ? "+" : ""}
          {ticker.changePercent.toFixed(1)}%
        </Typography>
      )}
    </Box>
  );
}

// ── Community row ────────────────────────────────────────────────────────────
function CommunityRow({ community, index }) {
  return (
    <Link
      to={`/c/${community.name}`}
      style={{ textDecoration: "none" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
          height: 52,
          borderRadius: "10px",
          backgroundColor: "#0f1419",
          cursor: "pointer",
          minWidth: 0,
          transition: "background-color 0.15s ease",
          "&:hover": { backgroundColor: "#242930" },
        }}
      >
        {/* Emoji + name / member count */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            flex: 1,
            overflow: "hidden",
          }}
        >
          <Typography sx={{ fontSize: "1.1rem", flexShrink: 0, lineHeight: 1 }}>
            {COMMUNITY_EMOJIS[index] ?? "🌐"}
          </Typography>
          <Box sx={{ minWidth: 0, overflow: "hidden" }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "0.875rem",
                color: "#f3f4f6",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                letterSpacing: "-0.01em",
              }}
            >
              c/{community.name}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#6a7282",
                whiteSpace: "nowrap",
              }}
            >
              {community.followersCount != null
                ? `${formatCount(community.followersCount)} members`
                : "Community"}
            </Typography>
          </Box>
        </Box>
        {/* Chevron */}
        <ChevronIcon sx={{ fontSize: 16, color: "#6a7282", flexShrink: 0, ml: 0.5 }} />
      </Box>
    </Link>
  );
}

// ── Contributor row ──────────────────────────────────────────────────────────
function ContributorRow({ user, index }) {
  const score =
    user.totalUpvotes ?? user.karmaScore ?? user.postCount ?? null;

  return (
    <Link
      to={`/profile/${user.username}`}
      style={{ textDecoration: "none" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
          height: 52,
          borderRadius: "10px",
          backgroundColor: "#0f1419",
          cursor: "pointer",
          minWidth: 0,
          transition: "background-color 0.15s ease",
          "&:hover": { backgroundColor: "#242930" },
        }}
      >
        {/* Rank emoji + username / post count */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            flex: 1,
            overflow: "hidden",
          }}
        >
          <Typography sx={{ fontSize: "1.3rem", flexShrink: 0, lineHeight: 1 }}>
            {RANK_EMOJIS[index] ?? "⭐"}
          </Typography>
          <Box sx={{ minWidth: 0, overflow: "hidden" }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "0.875rem",
                color: "#f3f4f6",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                letterSpacing: "-0.01em",
              }}
            >
              {user.username}
            </Typography>
            {user.postCount != null && (
              <Typography
                sx={{ fontSize: "0.75rem", color: "#6a7282", whiteSpace: "nowrap" }}
              >
                {formatCount(user.postCount)} posts
              </Typography>
            )}
          </Box>
        </Box>
        {/* Score + up arrow */}
        {score != null && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              flexShrink: 0,
              ml: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "#05df72",
                whiteSpace: "nowrap",
              }}
            >
              +{formatCount(score)}
            </Typography>
            <UpArrowIcon sx={{ fontSize: 12, color: "#05df72" }} />
          </Box>
        )}
      </Box>
    </Link>
  );
}

// ── Skeleton loader ──────────────────────────────────────────────────────────
function RowSkeleton({ height = 36, count = 4 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={height}
          sx={{ borderRadius: "10px", bgcolor: "rgba(255,255,255,0.06)" }}
        />
      ))}
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const LeftSidebar = () => {
  useTheme(); // keep theme subscription alive for future use
  const [trendingTickers, setTrendingTickers] = useState([]);
  const [trendingCommunities, setTrendingCommunities] = useState([]);
  const [trendingUsers, setTrendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [tickersRes, communitiesRes, usersRes] = await Promise.allSettled([
          fetch(`${API_BASE_URL}/api/trending/tickers?limit=4`),
          fetch(`${API_BASE_URL}/api/trending/communities?limit=4`),
          fetch(`${API_BASE_URL}/api/trending/users?limit=4`),
        ]);

        if (tickersRes.status === "fulfilled" && tickersRes.value.ok) {
          setTrendingTickers(await tickersRes.value.json());
        }
        if (communitiesRes.status === "fulfilled" && communitiesRes.value.ok) {
          setTrendingCommunities(await communitiesRes.value.json());
        }
        if (usersRes.status === "fulfilled" && usersRes.value.ok) {
          setTrendingUsers(await usersRes.value.json());
        }
      } catch (err) {
        console.error("[LeftSidebar] fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  return (
    <Box sx={{ width: "100%", pt: 2, px: 2, pb: 4, minWidth: 0, boxSizing: "border-box" }}>

      {/* ── Trending Tickers ─────────────────────────────────── */}
      {(loading || trendingTickers.length > 0) && (
        <SectionCard>
          <SectionHeader emoji="🔥" title="Trending Tickers" />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 1.5, pt: 1.5, pb: 1.5 }}>
            {loading ? (
              <RowSkeleton height={36} count={4} />
            ) : (
              trendingTickers.map((t) => (
                <TickerRow key={t.symbol ?? t.tickerId} ticker={t} />
              ))
            )}
          </Box>
        </SectionCard>
      )}

      {/* ── Top Communities ──────────────────────────────────── */}
      <SectionCard>
        <SectionHeader
          icon={GroupsIcon}
          title="Top Communities"
          gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 1.5, pt: 1.5, pb: 1.5 }}>
          {loading ? (
            <RowSkeleton height={52} count={4} />
          ) : trendingCommunities.length > 0 ? (
            trendingCommunities.map((c, i) => (
              <CommunityRow key={c.communityId ?? c.name} community={c} index={i} />
            ))
          ) : (
            <Typography sx={{ fontSize: "0.8rem", color: "#6a7282", py: 1, textAlign: "center" }}>
              No communities yet
            </Typography>
          )}
        </Box>
      </SectionCard>

      {/* ── Top Contributors ─────────────────────────────────── */}
      <SectionCard>
        <SectionHeader
          icon={PersonIcon}
          title="Top Contributors"
          gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 1.5, pt: 1.5, pb: 1.5 }}>
          {loading ? (
            <RowSkeleton height={52} count={4} />
          ) : trendingUsers.length > 0 ? (
            trendingUsers.map((u, i) => (
              <ContributorRow key={u.id ?? u.username} user={u} index={i} />
            ))
          ) : (
            <Typography sx={{ fontSize: "0.8rem", color: "#6a7282", py: 1, textAlign: "center" }}>
              No contributors yet
            </Typography>
          )}
        </Box>
      </SectionCard>
    </Box>
  );
};

export default LeftSidebar;
